import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { ChevronRight, FileText, IdCard } from "lucide-react";

import { loadMyCard } from "@/braining/partners.functions";
import { loadMyDeals } from "@/braining/deals.functions";
import { closingOpen, DEAL_STATUS_FOR_PARTNER } from "@/braining/deals";
import { PaperOriginals } from "@/braining/components/paper-originals";
import { UpdMaker } from "@/braining/components/upd-maker";
import { completeness } from "@/braining/partner-form";
import { REQ_STATE_LABEL } from "@/braining/partner-requisites";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { PartnerTelegramCard } from "@/braining/components/partner-telegram-card";

/**
 * «Документы» — главная кабинета партнёра, реестр его договоров, приложений,
 * счетов и актов.
 *
 * Реестр — сделки агентства с этим подрядчиком (этап 5), кроме черновиков:
 * договор или приложение, проект, сумма договора, где он сейчас. Сверху —
 * один следующий шаг, если он есть: заполнить реквизиты, поправить по
 * замечанию. Без реквизитов документ не собрать, поэтому они здесь, а не
 * только в своём разделе.
 *
 * У подписанного договора — закрывающие (созвон с Дашей 28.10): подрядчику
 * на бумаге — акт на подпись и трек-номер отправленных оригиналов, на ЭДО —
 * УПД в XML для его ЭДО.
 */
export const Route = createFileRoute("/cabinet")({
  head: () => ({ meta: [{ title: "Документы — кабинет партнёра Braining" }] }),
  loader: async () => {
    const [card, deals] = await Promise.all([loadMyCard(), loadMyDeals()]);
    return { card, deals };
  },
  component: DocumentsPage,
});

const COLUMNS = ["Документ", "Проект", "Сумма", "Статус"];

function DocumentsPage() {
  const { card, deals } = Route.useLoaderData();
  const router = useRouter();
  if (!card) {
    return (
      <p className="text-sm text-muted-foreground">
        Карточка не найдена. Напишите тому, кто вас приглашал.
      </p>
    );
  }
  const req = card.requisites;
  const done = completeness(card.kind, req.values);
  const needsAction = req.state === "draft" || req.state === "returned";
  // Без ЭДО — бумага: так по умолчанию, пока подрядчик не выбрал ЭДО в анкете.
  const onPaper = !(req.values.docFlow ?? "").startsWith("ЭДО");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Документы</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {card.name} · договоры, приложения, счета и акты по работе с Braining.
        </p>
      </div>

      {needsAction && (
        <Card className={cn(req.state === "returned" && "border-warning/50")}>
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center gap-2 font-medium">
              <IdCard className="size-4 text-muted-foreground" />
              {req.state === "returned" ? "Реквизиты вернули на доработку" : "Сначала — реквизиты"}
            </div>
            {req.state === "returned" && req.review?.note ? (
              <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-ink">
                Что поправить: {req.review.note}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Без них агентство не соберёт договор. Заполните один раз — дальше они подставятся во
                все документы сами. Заполнено {done.filled} из {done.total}.
              </p>
            )}
            <Button asChild>
              <Link to="/cabinet/requisites">
                {req.state === "returned"
                  ? "Поправить реквизиты"
                  : done.filled
                    ? "Дозаполнить"
                    : "Заполнить"}
                <ChevronRight className="size-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {!needsAction && (
        <p className="text-sm text-muted-foreground">
          Реквизиты: {REQ_STATE_LABEL[req.state].toLowerCase()}.
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-4 gap-3 border-b px-5 py-2.5 text-xs text-muted-foreground">
            {COLUMNS.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
          {deals.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
              <FileText className="size-6 text-muted-foreground" />
              <p className="text-sm font-medium">Документов пока нет</p>
              <p className="max-w-sm text-sm text-muted-foreground">
                Когда агентство подготовит договор или приложение, он появится здесь — посмотреть и
                согласовать можно будет прямо в кабинете.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {deals.map((d) => (
                <div key={d.id} className="px-5 py-3">
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <span>
                      {d.title}
                      {d.entityName && (
                        <span className="block text-xs text-muted-foreground">
                          с {d.entityName}
                        </span>
                      )}
                    </span>
                    <span className="min-w-0 break-words">
                      {d.project}
                      {d.services && (
                        <span className="block text-xs text-muted-foreground">{d.services}</span>
                      )}
                    </span>
                    <span className="tabular-nums">
                      {d.amountTotal == null
                        ? "—"
                        : `${d.amountTotal.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`}
                    </span>
                    <span className="text-muted-foreground">
                      {DEAL_STATUS_FOR_PARTNER[d.status]}
                    </span>
                  </div>
                  {(d.kind === "one_off" || d.kind === "annex") && closingOpen(d.status) && (
                    <div className="mt-3 rounded-md border bg-muted/30 p-3">
                      {onPaper ? (
                        <PaperOriginals deal={d} onSent={() => void router.invalidate()} />
                      ) : (
                        <UpdMaker deal={d} onMade={() => void router.invalidate()} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <PartnerTelegramCard />

      <p className="text-xs text-muted-foreground">
        Забыли пароль — попросите новую ссылку у того, кто вас приглашал.
      </p>
    </div>
  );
}
